import { useEffect, useRef, useState } from 'react';
import type { PortalMe } from '@bbbk/schema';
import { api, ApiError } from '../lib/api';
import { isoDate, startOfDay } from '../lib/dates';
import { RegistrationDetails, type DetailsDraft } from './RegistrationDetails';
import { RegistrationDogs, blankAnswers, claimedDates, type DogAnswers } from './RegistrationDogs';
import { RegistrationReceipt } from './RegistrationReceipt';
import type { StepError } from './RegistrationChrome';
import { dogsStepAction, dogsStepBlocker, type DogsBlock } from './registrationRules';
import { useCustomerToast } from './CustomerToast';

/**
 * What to tell the customer, naming the dog, so they know where to look.
 *
 * NEVER OFFERS A REMOVAL (PR #231 review, item 34): the well has a picker and
 * no remove control. A refused file is already detached, and a card missing a
 * date needs the date typed — both are things the screen can actually do.
 */
export function blockedMessage(blocked: DogsBlock, dog: string): string {
  const { field, why } = blocked;
  // The well draws a control to let the refused card go (PR #228 review), so
  // the message may offer it — list item 34 was about offering what the screen
  // did not have.
  if (field === 'file') return `${dog}'s card wasn't accepted — choose another photo or PDF, or carry on without it.`;
  if (field === 'name') return `${dog} needs a name before you can finish.`;
  if (field === 'instructions') return `Tell us ${dog}'s medication, or switch it back to None.`;
  const which = field === 'validUntil' ? 'annual' : 'kennel cough';
  // TWO FAULTS, TWO THINGS TO SAY (Freddi, 2026-09-17). *Finish* is right for
  // `03/2` sitting in the field where the customer can see it, and wrong for an
  // empty one — telling somebody to finish a field they never started reads as
  // the app having lost their typing. The empty case also has to say WHY it is
  // suddenly being asked for, because a moment ago it was optional: the card is
  // the reason, and naming it is the difference between a rule and a nag.
  if (why === 'required') {
    return `${dog}'s card needs its ${which} date beside it — add the month and year, like 03/27.`;
  }
  return `Finish ${dog}'s ${which} date — it needs a month and a year, like 03/27.`;
}

// THE WIZARD — details, dogs, receipt (customer-sign-in-registration).
//
// Each step is its own request and `/me` is re-read after each, so the next
// step draws from what the server holds rather than from what the browser
// remembers: a customer who closes the tab on *Your details* comes back to it
// prefilled from the contact (AC2, AC15), and the receipt is what the server
// has, not what was typed (AC30–AC35).

/** The page title on every step (AC3). */
export const REGISTRATION_TITLE = 'Registration';

type Step = 'details' | 'dogs' | 'receipt';

/** The only field this wizard draws an inline error against (AC14). */
const DRAWN_FIELDS = new Set(['firstName']);

/**
 * THE 409 IS THE PROOF THE WRITE IS IN (PR #231 review, item 33). The final
 * POST committed and its reply was lost; the retry re-submits and `registered_at`
 * refuses it. That refusal is not a failure to report — it says registration
 * landed, and the only thing left is the read.
 */
export function landedDespite(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409;
}

export function failureOf(e: unknown): StepError {
  if (e instanceof ApiError && e.status === 429) return { kind: 'busy' };
  // A FIELD ERROR NOBODY DRAWS IS A BLANK SCREEN (Codex review, PR #228). This
  // mapped EVERY 400 carrying a field to the field state, and `StepFailure`
  // renders nothing for that state — so an upload the server refused (a
  // zero-byte photo passes the client's own check and comes back
  // `field: "file"`) left the customer on the dogs step with no error at all,
  // wondering why the button did nothing. Only the field the wizard actually
  // marks stays a field error; everything else falls through to the message
  // that IS drawn, with Patricia beneath it.
  if (e instanceof ApiError && e.status === 400 && e.field && DRAWN_FIELDS.has(e.field)) {
    return { kind: 'field', field: e.field };
  }
  return { kind: 'other' };
}

export interface RegistrationProps {
  me: PortalMe;
  /** Re-read `/me`; resolves to the fresh shape or null when it failed. */
  reload: () => Promise<PortalMe | null>;
  /** Registration just completed in this tab — keep the receipt up. */
  onRegistered: () => void;
  /** The receipt's button was tapped — Home now. */
  onDone: () => void;
}

export function Registration({ me, reload, onRegistered, onDone }: RegistrationProps) {
  const toast = useCustomerToast();
  // Already registered (this tab just did it, or /me says so): the receipt.
  const [step, setStep] = useState<Step>(me.contact.registeredAt ? 'receipt' : 'details');
  const [draft, setDraft] = useState<DetailsDraft>({
    firstName: me.contact.firstName ?? '',
    lastName: me.contact.lastName ?? '',
    address: me.address ?? '',
  });
  const [answers, setAnswers] = useState<DogAnswers>(() => blankAnswers(me));
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState<string | null>(null);
  const [error, setError] = useState<StepError | null>(null);
  /** The dog a refused submit was about, so its card can be opened. */
  const [blockedDog, setBlockedDog] = useState<string | undefined>(undefined);
  // THE KENNEL'S DAY, NOT THE PHONE'S (P2.32, 2026-09-15). A wrong clock or a
  // timezone boundary moved a MEDICAL date between valid and lapsed. The
  // browser is the fallback only for a payload that predates the field.
  const today = me.today || isoDate(startOfDay(new Date()));

  useEffect(() => {
    const was = document.title;
    document.title = REGISTRATION_TITLE;
    return () => {
      document.title = was;
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const submitDetails = async () => {
    if (busy) return;
    if (!draft.firstName.trim()) {
      setError({ kind: 'field', field: 'firstName' });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/portal/registration/details', { method: 'POST', body: draft, redirectOn401: false });
      const fresh = await reload();
      if (fresh) setAnswers((prev) => ({ ...blankAnswers(fresh), ...prev }));
      setStep('dogs');
    } catch (e) {
      setError(failureOf(e));
    } finally {
      setBusy(false);
    }
  };

  // REGISTRATION HAS LANDED; ONLY THE RE-READ IS LEFT (Codex, PR #228).
  //
  // The previous fix kept the customer on the step when the post-write reload
  // failed — but the step's only action RE-SUBMITS, and `registered_at` is
  // already stamped, so the retry 409s and `reload()` is never reached again.
  // They are stranded on a screen whose button cannot work. This flag turns
  // that same button into a retry of the read, which is the only thing that
  // actually failed.
  const landedRef = useRef(false);

  // WHAT THE CUSTOMER HAS TYPED *NOW*, not when they pressed the button.
  //
  // The submit awaits one upload per dog, and nothing disables the fields while
  // it does — `busy` only spins the button. So a correction made during the
  // uploads, or a file swapped after its dog's turn had passed, was written
  // over by the snapshot the handler had closed over, and the customer watched
  // their own edit vanish with no error (issues 8, 9, 2026-09-15).
  const answersRef = useRef(answers);
  answersRef.current = answers;

  const submitDogs = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // The write is done; do not send it again. `registered_at` is stamped, so
      // a re-POST answers 409 and the customer never gets past this screen.
      if (dogsStepAction(landedRef.current) === 'reread') {
        const again = await reload();
        if (!again) { setError({ kind: 'other' }); return; }
        onRegistered();
        setStep('receipt');
        return;
      }
      // NOTHING GOES UP THAT CANNOT BE SENT. Registration is once-only, so a
      // value the payload would silently drop has to stop the submit instead
      // (2026-09-15). The step points at the dog and the field.
      const blocked = dogsStepBlocker(me.dogs, answersRef.current);
      if (blocked) {
        const dog = me.dogs.find((d) => d.id === blocked.dogId);
        // AND OPEN THE CARD IT IS ABOUT (PR #228 review). The message names the
        // dog; on a household with several, every other card is folded to a
        // row, so the customer was told to fix a field they could not see and
        // had to guess which row to tap. `RegistrationDogs` has had an `open`
        // prop for exactly this and nothing ever passed it.
        setBlockedDog(blocked.dogId);
        setError({ kind: 'incomplete', message: blockedMessage(blocked, dog?.name ?? 'that dog') });
        return;
      }
      // Cards first, one request each, so a failed upload never loses the
      // answers (AC26); then the once-only registration (AC28).
      for (const dog of me.dogs) {
        const a = answersRef.current[dog.id];
        if (!a?.file) continue;
        setSending(dog.id);
        const form = new FormData();
        form.set('file', a.file);
        const dates = claimedDates(a);
        if (dates.validUntil) form.set('valid_until', dates.validUntil);
        if (dates.kcUntil) form.set('kc_until', dates.kcUntil);
        await api(`/portal/dogs/${encodeURIComponent(dog.id)}/card`, { method: 'POST', form, redirectOn401: false });
        toast({ title: 'Vaccination card added', detail: `${dog.name}’s card is with Patricia` });
        setAnswers((prev) => ({ ...prev, [dog.id]: { ...prev[dog.id]!, file: null } }));
      }
      setSending(null);
      await api('/portal/registration', {
        method: 'POST',
        redirectOn401: false,
        body: {
          dogs: me.dogs.map((dog) => {
            // Current, not captured — see answersRef above (issue 8).
            const a = answersRef.current[dog.id];
            return {
              id: dog.id,
              // Prefilled from the desk, corrected by the owner (Freddi,
              // 2026-09-14). Falls back to what the desk holds so a dog whose
              // card was never opened is never blanked.
              name: (a?.name ?? dog.name).trim() || dog.name,
              breed: (a?.breed ?? dog.breed ?? '').trim() || null,
              meds: a?.meds === 'yes' ? a.instructions.trim() || null : null,
              keepApart: a?.apart === 'apart',
              // The dates ride with the answers now, not only with a card
              // upload — see RegistrationDogs (Freddi, 2026-09-16).
              ...(a ? claimedDates(a) : {}),
            };
          }),
        },
      });
      // THE RECEIPT IS SHOWN ONCE, SO IT IS SHOWN FROM A FRESH READ (Codex
      // review, PR #228). `reload()` resolves to null when the re-read fails,
      // and advancing anyway meant the parent drew its locked-out page instead
      // — after which `registered_at` sends the customer straight to Home
      // forever. One transient failure and the receipt they registered for is
      // gone for good. Registration HAS landed at this point, so this is not a
      // failure to report as one: keep them on the step with a retry.
      landedRef.current = true;
      const fresh = await reload();
      if (!fresh) {
        setError({ kind: 'other' });
        return;
      }
      onRegistered();
      setStep('receipt');
    } catch (e) {
      setSending(null);
      if (landedDespite(e)) {
        // Registration is in; only the reply was lost. Read, and if the read
        // fails too, the button is now a retry of the read (landedRef).
        landedRef.current = true;
        const fresh = await reload();
        if (fresh) {
          onRegistered();
          setStep('receipt');
          return;
        }
        setError({ kind: 'other' });
        return;
      }
      setError(failureOf(e));
      // A CARD THAT LANDED MUST STILL SHOW. The uploads are separate requests
      // and each clears its file from the draft on success; if the registration
      // POST then failed, the well went back to *Add the card* while the proof
      // sat in R2 and the customer re-picked a file we already had (issue 11,
      // 2026-09-15). Re-reading puts the stored card back on screen. Best
      // effort — the error above is what they act on either way.
      void reload();
    } finally {
      setBusy(false);
    }
  };

  if (step === 'receipt') {
    return (
      <div onClickCapture={(e) => {
        // The receipt's one button lands on Home — inside this tab, without a
        // reload, so the receipt stays the last thing they saw of registration.
        const a = (e.target as HTMLElement).closest('a[href="/home"]');
        if (a) {
          e.preventDefault();
          onDone();
        }
      }}>
        <RegistrationReceipt me={me} today={today} />
      </div>
    );
  }
  if (step === 'dogs') {
    return (
      <RegistrationDogs
        me={me}
        answers={answers}
        onChange={setAnswers}
        onSubmit={() => void submitDogs()}
        onBack={() => {
          if (busy) return;
          setError(null);
          setStep('details');
        }}
        onOpen={() => setBlockedDog(undefined)}
        busy={busy}
        sending={sending}
        error={error}
        today={today}
        open={blockedDog}
      />
    );
  }
  return (
    <RegistrationDetails
      me={me}
      draft={draft}
      onChange={(d) => {
        setDraft(d);
        if (error?.kind === 'field') setError(null);
      }}
      onSubmit={() => void submitDetails()}
      busy={busy}
      error={error}
    />
  );
}
